import { EthHashInfo, Identicon } from '@gnosis.pm/safe-react-components'
import classNames from 'classnames'
import { Fragment, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ListItemType } from 'src/components/List'
import { getChainInfo, getExplorerInfo } from 'src/config'
import { border, fontColor } from 'src/theme/variables'
import { useMain } from 'src/walliro/hook'
import styled from 'styled-components'
import styles from './style.module.scss'
import { ChainInfo } from '@gnosis.pm/safe-react-gateway-sdk'
import { useSelector } from 'react-redux'
import { copyShortNameSelector, showShortNameSelector } from 'src/logic/appearance/selectors'
import { parsePrefixedAddress } from 'src/utils/prefixedAddress'
import useSafeAddress from 'src/logic/currentSession/hooks/useSafeAddress'
import { Dropdown } from 'react-bootstrap'
import truncate from 'src/walliro/constants/truncate'
import CustomNav from './Nav'
import Networks from './Netwroks'

type Props = {
  safeAddress?: string
  safeName?: string
  balance?: string
  granted: boolean
  onToggleSafeList: () => void
  onReceiveClick: () => void
  onNewTransactionClick: () => void
  items: ListItemType[]
}

type StyledTextLabelProps = {
  chainInfo: ChainInfo
}
const StyledTextLabel = styled.span`
  color: ${(props: StyledTextLabelProps) => props.chainInfo?.theme?.textColor ?? fontColor};
  background-color: ${(props: StyledTextLabelProps) => props.chainInfo?.theme?.backgroundColor ?? border};
`

const SidebarComponent = ({
  items,
  balance,
  safeAddress,
  safeName,
  granted,
  onToggleSafeList,
  onReceiveClick,
  onNewTransactionClick,
}: Props) => {
  const { sidebar } = useMain()
  const _refSidebar = useRef(null)
  const isOpen = useHover(_refSidebar)
  const handlerSidebarOpen = sidebar.setOpen
  const handlerResizeWin = useCallback(() => {
    handlerSidebarOpen(window.innerWidth > 991)
  }, [handlerSidebarOpen])
  useEffect(() => {
    handlerResizeWin()
    window.addEventListener('resize', handlerResizeWin)
    return () => {
      window.removeEventListener('resize', handlerResizeWin)
    }
  }, [handlerResizeWin])

  const chainInfo = getChainInfo()

  const showChainPrefix = useSelector(showShortNameSelector)
  const copyChainPrefix = useSelector(copyShortNameSelector)
  const { address } = parsePrefixedAddress(safeAddress)
  const { shortName } = useSafeAddress()
  const explorerUrl = getExplorerInfo(safeAddress ? safeAddress : '')
  const explorerUrlSafeAddress = useMemo(() => {
    return explorerUrl()
  }, [])
  return (
    <Fragment>
      <div className={classNames(styles.sidebar, { [styles.close]: !isOpen })} ref={_refSidebar}>
        {safeAddress && (
          <div className={styles.profile}>
            <div className={styles.avatar}>
              <Identicon address={safeAddress} size="lg" />
              <div className={styles.badge}>
                <StyledTextLabel chainInfo={chainInfo}>{chainInfo.chainName}</StyledTextLabel>
              </div>
            </div>
            <div className={classNames(styles['content'], 'ps-3')}>
              {safeName && <h6 className="fz-14 fw-bold mb-1 color-text">{truncate(safeName, 15)}</h6>}
              <EthHashInfo
                hash={address}
                shortName={shortName}
                shouldShowShortName={showChainPrefix}
                // shouldCopyShortName={copyChainPrefix}
                shortenHash={3}
                showCopyBtn
                textColor="inputFilled"
              />
              {!granted && <p className="mb-0 color-blue fw-bold fz-12">READ ONLY</p>}
            </div>
            <div className="d-flex flex-column">
              <Dropdown className={styles.btn}>
                <Dropdown.Toggle variant="shadow" className={'btn-icon fixed btn-circle'}>
                  <i className="material-icons">more_vert</i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item disabled={!granted} onClick={onNewTransactionClick}>
                    <i className="material-icons me-3">north_east</i>
                    New transaction
                  </Dropdown.Item>
                  <Dropdown.Item onClick={onReceiveClick}>
                    <i className="material-icons me-3">qr_code</i>
                    Show Qr
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={'a'}
                    target={'_blank'}
                    href={explorerUrlSafeAddress.url}
                    className={styles['item']}
                  >
                    <i className="material-icons me-3">open_in_new</i>
                    {explorerUrlSafeAddress.alt}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        )}
        <div className="p-3">
          <Networks sidebarIsOpen={isOpen} />
        </div>
        <CustomNav list={items} />
      </div>
    </Fragment>
  )
}

function useHover<T extends HTMLElement = HTMLElement>(elementRef: RefObject<T>): boolean {
  const cursorXPosition = useRef(false)
  const { sidebar } = useMain()
  const isOpen = sidebar.open
  const [value, setValue] = useState<boolean>(isOpen)
  const handleMouseEnter = () => setValue(true)
  const handleMouseLeave = () => setValue(false)
  const handleMouseMove = useCallback((event: any) => {
    try {
      const position = event.currentTarget.offsetLeft + event.currentTarget.offsetWidth / 2
      const eventX = event.touches && event.touches.length > 0 ? event.touches[0].clientX : 0
      cursorXPosition.current = eventX > position
    } catch {}
  }, [])
  const handleMouseUp = useCallback(() => {
    setValue(cursorXPosition.current)
    const node = elementRef?.current
    if (node) {
      node.removeEventListener('touchmove', handleMouseMove, false)
    }
  }, [elementRef, handleMouseMove, cursorXPosition])

  useEffect(() => {
    setValue(isOpen)
  }, [isOpen])

  useEffect(() => {
    const node = elementRef?.current
    if (node && !isOpen) {
      node.addEventListener('mouseenter', handleMouseEnter)
      node.addEventListener('mouseleave', handleMouseLeave)

      node.addEventListener('touchstart', function () {
        this.addEventListener('touchmove', handleMouseMove, false)
        this.addEventListener('touchend', handleMouseUp, false)
      })
      return () => {
        node.removeEventListener('mouseenter', handleMouseEnter)
        node.removeEventListener('mouseleave', handleMouseLeave)
        node.removeEventListener('touchstart', function () {
          this.removeEventListener('touchmove', handleMouseMove, false)
          this.removeEventListener('touchend', handleMouseUp, false)
        })
      }
    }
  }, [handleMouseMove, handleMouseUp, elementRef, isOpen])

  return value
}
export default SidebarComponent
